import React, { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { useMutation } from 'react-query';
import { CLICK_EXTERNAL_BOOKING_FLOW_LINK } from '~/core/analytics/events';
import { getImageObject, LOGO } from '~/components/SolvPatternLibrary/Picture/util';
import { formatLocationName, getCdpDisplayName } from '~/core/util/location';
import Logo from '~/components/UniversalDetailsPage/components/HeaderPanel/Logo';
import LinkOutModal from '~/components/LinkOutModal/LinkOutModal';
import { apiPostJson } from '~/core/dapi';
import { ANALYTICS, DAPI_HOST } from '../../config';
import {
  fireGoogleAnalytics,
  GAEventType,
  GAEventAction,
  isConfiguredForGoogleAnalyticsTracking,
  GAEventCategory,
} from '~/core/analytics/trackingForPartners';
import { analyticsTrackEvent } from '~/core/analytics';
import { useLocationGeneralConfigs } from '~/hooks/useLocationGeneralConfigs';

type Props = {
  isOpen: boolean;
  location: LocationInterface;
  onClose: () => void;
  reasonForVisit?: string;
  appointmentTime?: number;
};

type Response = {
  redirect_url: string;
};

const DynamicLinkOutModal = (props: Props) => {
  const { appointmentTime, isOpen, location, onClose } = props;
  const locationName = formatLocationName(location);
  const logoImageObject = getImageObject(location, { imageType: LOGO });
  const [redirectUrl, setRedirectUrl] = useState<string>();

  const { isLoading, data: customMessageConfig } = useLocationGeneralConfigs(
    location.id,
    ['externalBookingCustomMessage'],
    false
  );
  const useDefaultMessage = !isLoading && !customMessageConfig?.externalBookingCustomMessage?.en;
  const description = useDefaultMessage
    ? `You’re one step closer to trusted care with ${getCdpDisplayName(
        location
      )}. Click now to complete booking your appointment. It’s fast, easy, and secure.`
    : customMessageConfig?.externalBookingCustomMessage?.en ?? '';

  const setFallbackBookingUrl = () => {
    setRedirectUrl(`${location.externalBookingUrl}?tracking_id=UA-83223001`);
  };

  const saveAppointment = useMutation(
    () => {
      return apiPostJson<Response>(`${DAPI_HOST}/v1/registration/booking-holds/`, {
        appointment_date: appointmentTime,
        location_id: location.id,
      });
    },
    {
      onSuccess: (data) => {
        if (!isEmpty(data.errors)) {
          setFallbackBookingUrl();
        } else {
          setRedirectUrl(data.data?.redirect_url);
        }
      },
      onError: () => {
        setFallbackBookingUrl();
      },
    }
  );

  useEffect(() => {
    if (ANALYTICS.enabled && window.analytics) {
      analytics.ready(() => {
        window.ga?.('require', 'linker');
        window.ga?.('linker:autoLink', [
          location.trackingProperties?.google_analytics?.referring_domain,
        ]);
      });
    }
  }, []);

  useEffect(() => {
    if (appointmentTime && isOpen) {
      saveAppointment.mutate();
    }
  }, [appointmentTime, isOpen]);

  const getLogo = () => {
    if (logoImageObject.fullRes) {
      return <Logo altText={`${locationName} logo`} imageObject={logoImageObject as any} />;
    }
    return undefined;
  };

  return (
    <LinkOutModal
      ctaClickEvent={CLICK_EXTERNAL_BOOKING_FLOW_LINK}
      ctaClickHandler={() => {
        if (isConfiguredForGoogleAnalyticsTracking(location)) {
          analyticsTrackEvent(GAEventCategory.EXTERNAL_BOOKING, {
            location_id: location.id,
            external_booking_url: location.externalBookingUrl,
          });
          fireGoogleAnalytics(
            location,
            GAEventType.GA_EVENT,
            GAEventAction.EXTERNAL_BOOKING,
            GAEventCategory.EXTERNAL_BOOKING
          );
        }
      }}
      description={description}
      isOpen={isOpen}
      locationName={locationName}
      logo={getLogo()}
      onClose={onClose}
      redirectUrl={redirectUrl}
    />
  );
};

export default DynamicLinkOutModal;
